import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { FormItemProps, ItemProps } from './types';
import Container from './elements/container';
import * as Yup from 'yup';
import {v4 as uuidv4} from "uuid";

export interface FormGeneratorHandles {
    getData: () => Record<string, any>;
    setData: (newData: Record<string, any>) => void;
    updateValue: (name: string, value: any) => void;
    changeInput: (name: string, value: any) => void;
    validate: (hideAlert?:boolean, data?:any) => Promise<Array<{ name: string; message: string }> | null>;
    updateForm: (itemName: string, attributeName: string, attributeValue: any) => void;
	reset: (name?: string) => void;
}

interface FormProps {
    items: Array<FormItemProps>;
    onChange?: (formData: Record<string, any>) => void;
    defaultValues?: Record<string, any>;
	liveValidate?: boolean;
}

const FormGenerator = forwardRef<FormGeneratorHandles, FormProps>(({ items = [], onChange, defaultValues = {}, liveValidate = false }, ref) => {

    const initialValues: Record<string, any> = {
        ...Object.fromEntries(
            items.map(item => [
                item.name,
                item.defaultValue !== undefined ? item.defaultValue : null
            ])
        ),
        ...defaultValues
    };

    const [formData, setFormData] = React.useState<Record<string, any>>(initialValues);
    const [initialized, setInitialized] = React.useState(false);
    const [itemList, setItemList] = React.useState<Array<ItemProps>>([]);
    const [formId, setFormId] = React.useState<string>(`form-${Math.random().toString(36).substring(2, 15)}`);
    const latestFormData = React.useRef(initialValues);



    const updateData = (name: string, value: any) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setItemList(prev => {
            const next = prev.map(i => (i.name === name ? { ...i, value } : i)) as ItemProps[];
            const target = next.find(i => i.name === name);
            if (target?.formItem?.validation && liveValidate) {
                handleValidateItem(name, target.formItem.validation, value);
            }
            return next;
        });
    };

    const handleInputChange = (name: string, value: any) => {
        updateData(name, value);
        handleItemChangeCallback(name, value);
        onChange?.({ ...latestFormData.current, [name]: value });
    };

    const updateForm = (itemName: string, attributeName: string, attributeValue: any) => {
        setItemList((prevList) => {
            const index = prevList.findIndex((i) => i.name === itemName);
            if (index === -1) {
                console.error("Item not found:", itemName);
                return prevList;
            }

            const itemData = prevList[index];

            const newItem = {
                ...itemData,
                formItem: {
                    ...itemData.formItem,
                    itemOptions: {
                        ...itemData.formItem.itemOptions,
                        [attributeName]: attributeValue
                    }
                }
            };

            const updatedList = [...prevList];
            updatedList[index] = newItem;
            return updatedList;
        });
    };


    const changeProps = (name: string, value: any) => ({
        formData: formData,
        value: value,
        updateValue: (itemName: string, itemValue: any) => {
            if (name === itemName) {
                updateData(itemName, itemValue)
            } else {
                handleInputChange(itemName, itemValue)
            }
        },
        updateForm: updateForm
    });

    const handleItemChangeCallback = (name: string, value: any) => {
        const index = itemList.findIndex((item) => item.name === name);
        const item = itemList[index];
        if (item?.formItem?.onChange) {
            item.formItem.onChange(changeProps(name, value));
        }
    };

    const handleValidateItem = async (itemName: string, validationCondition: Yup.Schema<any> | undefined | null, innerValue:any = '', hideAlert?:boolean) => {
        const errors: Array<{ name: string; message: string }> = [];

        if (validationCondition) {
            const validator = Yup.object().shape({ [itemName]: validationCondition });

            try {
                await validator.validate({ [itemName]: innerValue });

                if (!hideAlert) {
                    setItemList((prev) => {
                        const updatedItemList = [...prev];
                        const index = updatedItemList.findIndex((i) => i.name === itemName);
                        if (index !== -1) {
                            updatedItemList[index] = { ...updatedItemList[index], validation: null };
                        }
                        return updatedItemList;
                    });
                }

            } catch (err) {
                errors.push({ name: itemName, message: (err as Yup.ValidationError).message });

                if (!hideAlert) {
                    setItemList((prev) => {
                        const updatedItemList = [...prev];
                        const index = updatedItemList.findIndex((i) => i.name === itemName);
                        if (index !== -1) {
                            updatedItemList[index] = { ...updatedItemList[index], validation: (err as Yup.ValidationError).message };
                        }
                        return updatedItemList;
                    });
                }
            }
        }

        return errors.length > 0 ? errors : null;
    };

    const handleValidateItems = async (hideAlert?: boolean, data?: Record<string, any>) => {
        const src = data ?? latestFormData.current;
        const errors: Array<{ name: string; message: string }> = [];
        for (const item of items) {
            const innerValue = src[item.name] ?? '';
            const itemErrors = await handleValidateItem(item.name, item.validation, innerValue, hideAlert);
            if (itemErrors) errors.push(...itemErrors);
        }
        return errors.length > 0 ? errors : null;
    };



    const handleCreateItem = (item: FormItemProps): ItemProps => ({
        formItem: item,
        name: item.name,
        label: item.label || '',
        type: item.type,
        validation: null,
        onChange: (name: string, value: any) => handleInputChange(name, value),
        updateForm:updateForm,
        onFocus: () => {},
        onBlur: () => {},
        onKeyDown: () => {},
        onClick: () => {},
        autoFocus: item.autoFocus || false,
        condition: item.condition,
        disabled: item.disabled || false,
        classNames: item.classNames || {},
        formData: formData,
        formId: formId,
	    validate: handleValidateItems
    });

    useEffect(() => {
        if (!initialized) {
            const uuid = uuidv4();
            setFormId(uuid);
            const generatedItems: Array<ItemProps> = items.map((item) => {
                // Only use form item's defaultValue if no value was provided via defaultValues prop
                if (item.defaultValue !== undefined && defaultValues[item.name] === undefined) {
                    handleInputChange(item.name, item.defaultValue);
                }
                return handleCreateItem(item);
            });
            setItemList(generatedItems);
            setInitialized(true);

        }
    }, [initialized, items]);
    useEffect(() => { latestFormData.current = formData; }, [formData]);


    useImperativeHandle(ref, () => ({
        getData: () => latestFormData.current,
        setData: (newData: Record<string, any>) => {
            setFormData(newData);
            latestFormData.current = newData;
            setItemList(prev => prev.map(i => ({ ...i, value: newData[i.name] })));
        },
        updateValue: (name: string, value: any) => updateData(name, value),
        updateForm,
        changeInput: handleInputChange,

        validate: (hideAlert?: boolean, data?:any) => handleValidateItems(hideAlert, data || latestFormData.current),
        reset: (name?: string) => {
            if (!name) {
                setFormData(defaultValues);
                latestFormData.current = defaultValues;
                setItemList(prev => prev.map(i => ({ ...i, value: defaultValues[i.name] ?? null })));
            } else {
                setFormData(prev => {
                    const nd = { ...prev };
                    delete nd[name];
                    latestFormData.current = nd;
                    return nd;
                });
                setItemList(prev => prev.map(i => (i.name === name ? { ...i, value: null } : i)));
            }
        }
    }));



	const renderItem = (item: ItemProps) => (
		<Container
			key={item.name}
			{...item}
			onChange={handleInputChange}
			formData={formData}
			formId={formId}
		/>
	);

	const visibleItems = itemList.filter((item: ItemProps) => {
		return item.formItem.condition ? item.formItem.condition(formData, itemList) : true
	});

	const hasFullWidthRows = itemList.some((item: ItemProps) => item.formItem.itemOptions?.features?.fullWidthRow);

	if (hasFullWidthRows) {
		const rows: Array<
			| { type: 'normal'; items: ItemProps[] }
			| { type: 'fullWidth'; item: ItemProps }
		> = [];

		visibleItems.forEach((item: ItemProps) => {
			if (item.formItem.itemOptions?.features?.fullWidthRow) {
				rows.push({ type: 'fullWidth', item });
				return;
			}

			const lastRow = rows[rows.length - 1];
			if (lastRow?.type === 'normal') {
				lastRow.items.push(item);
			} else {
				rows.push({ type: 'normal', items: [item] });
			}
		});

		return (
			<>
				{rows.map((row, index) => {
					if (row.type === 'fullWidth') {
						return renderItem(row.item);
					}

					return (
						<div
							key={`form-row-${formId}-${index}`}
							className="col-span-12 grid grid-cols-12 gap-xl w-full max-w-[1000px] justify-self-start"
						>
							{row.items.map(renderItem)}
						</div>
					);
				})}
			</>
		);
	}

    return (

        <>
            {itemList.map((item: ItemProps) => {
                const isVisible = item.formItem.condition ? item.formItem.condition(formData, itemList) : true


                if (!isVisible) return null;


                return renderItem(item);
            })}
        </>
    );
});

FormGenerator.displayName = 'FormGenerator';
export default FormGenerator;
